import QueueModalWrapper from "./QueueModalWrapper";
import {Button, TextArea} from "@varymadellc/my-weed-club-component-library";
import FileInput from "../../../components/FileInput/FileInput";
import React, {useEffect, useRef, useState} from "react";
import {useAuth} from "../../../providers/AuthProvider";
import {apiPost, extractPayloadFromResponse} from "../../../services/apiService";
import {useSiteMessage} from "../../../providers/SiteMessageProvider";
import Loading from "../../../components/Loading/Loading";
import setImagesOnUpload, {loadedImageType} from "../../../utils/setImagesOnUpload";
import imageExtensionIsSafe from "@/utils/imageExtensionIsSafe";

export interface QueueModalImageProps {
    setIsOpen: (value: string) => void;
    requestRefresh?: () => void;
}

const uploadImage = async (description: string, file: loadedImageType, authToken: string): Promise<{error?: string; data: any}> => {
    const fileData = await fetch(String(file.image));

    const fileBlob = await fileData.blob();

    const formData = new FormData();

    async function resizeImage(blob, maxWidth, maxHeight): Promise<Blob | null> {
        const image = await createImageBitmap(blob);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        let newWidth, newHeight;

        // Calculate new dimensions while preserving the aspect ratio
        if (image.width > image.height) {
            newWidth = Math.min(image.width, maxWidth);
            newHeight = (newWidth / image.width) * image.height;
        } else {
            newHeight = Math.min(image.height, maxHeight);
            newWidth = (newHeight / image.height) * image.width;
        }

        // Set the canvas dimensions
        canvas.width = newWidth;
        canvas.height = newHeight;

        if (!ctx) return null;

        // Draw the resized image on the canvas
        ctx.drawImage(image, 0, 0, newWidth, newHeight);

        return new Promise((resolve) => {
            canvas.toBlob((resultBlob) => {
                resolve(resultBlob);
            }, 'image/jpeg');
        });
    }

    const resizedBlob = await resizeImage(fileBlob, 1200, 1200);

    if (!resizedBlob) {
        throw new Error('Could not correct image orientation');
    }

    formData.append('file', resizedBlob, file.name);

    const fileResponse = await apiPost('/upload?type=queue-image', formData, authToken, {'Content-Type': false});

    const {data: filePayload} = await extractPayloadFromResponse(fileResponse);

    const response = await apiPost('/q', JSON.stringify({
        type: 'image',
        metadata: {
            src: filePayload.metadata.publicUrl,
            description: description || null,
            width: filePayload.metadata.width || null,
            height: filePayload.metadata.height || null,
        }
    }), authToken);

    return extractPayloadFromResponse(response);
};

export default function QueueModalImage({setIsOpen, requestRefresh}: QueueModalImageProps) {
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const setIsOpenHandler = (value: string) => {
        if (!value) {
            setFiles([]);

            setDescription('');

            setIsOpen(value || '');
        }
    }

    const {authToken} = useAuth();

    const {setMessage} = useSiteMessage();

    const [description, setDescription] = useState<string>('');

    const [files, setFiles] = useState<loadedImageType[]>([]);

    const fileRef = useRef<any>(null);

    const onSubmitHandler = (event: any) => {
        event.preventDefault();

        if (!fileRef?.current?.value) {
            setMessage('Please select an image');

            return;
        }

        async function postData() {
            try {
                if (!files || files.length < 1) return;

                if (!files.every((filePath: loadedImageType) => imageExtensionIsSafe(filePath.name))) {
                    setMessage('File type is not a support image format');

                    return;
                }

                setIsLoading(true);

                const queueItemsPromises = files.map(async (file) => {
                    return new Promise(async (resolve, reject) => {
                        try {
                            const {error, data} = await uploadImage(description, file, authToken);

                            if (error || !data) {
                                reject(error);

                                return;
                            }

                            resolve(data);
                        } catch(error: any) {
                            reject(error.message);
                        }
                    });
                });

                const queueItems = await Promise.all(queueItemsPromises);

                if (queueItems.length > 0) {
                    setMessage('Images added to Queue');

                    setIsOpen('');

                    requestRefresh && requestRefresh();
                }

                setIsLoading(false);
            } catch(error: any) {
                setIsLoading(false);
                setMessage(error.message);
            }
        }

        postData();
    };

    return (
        <QueueModalWrapper setIsOpen={setIsOpenHandler}>
            <>
                {isLoading && <Loading hasOverlay={true}/>}
                <div className="relative flex flex-col">
                    <h2>Add Images</h2>
                    <hr className="mt-2"/>
                    <form onSubmit={onSubmitHandler} className="flex flex-col gap-4">
                        <label htmlFor="image">Select your images</label>
                        <FileInput name="galleryImage"
                                      accept="image/*"
                                   multiple={true}
                                   type="file"
                                   _ref={fileRef}
                                   onChange={setImagesOnUpload({setNewImages: setFiles, setError: setMessage})}/>
                        <label htmlFor="Description">Description (optional)</label>
                        <TextArea onChange={({target: {value}}: any) => setDescription(value)}/>
                        <Button type="submit">Add</Button>
                    </form>
                </div>
            </>
        </QueueModalWrapper>
    );
}